import { useEffect, useState } from 'react';
import { serverNow } from '../../time-sync';
import type { GameSnapshot } from '../../socket';

// Soccer board: count-UP clock (m:ss), goal counters, cards line,
// stoppage indicator. Halves shown as "1st HALF" / "2nd HALF".

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function useLiveCountupClock(
  m: number,
  s: number,
  running: boolean,
  startedAt: string | null | undefined,
): { m: number; s: number } {
  const [, force] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running]);
  if (!running || !startedAt) return { m, s };
  const elapsed = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  const total = m * 60 + s + elapsed;
  return { m: Math.floor(total / 60), s: total % 60 };
}

export function SoccerScoreboard({
  snapshot,
  isSample,
  connected,
}: {
  snapshot: GameSnapshot | null;
  isSample: boolean;
  connected: boolean;
}) {
  const s = snapshot?.state;
  const home = snapshot?.rosters.home;
  const away = snapshot?.rosters.away;

  const homeScore = s?.home.score ?? (isSample ? 2 : 0);
  const awayScore = s?.away.score ?? (isSample ? 1 : 0);
  const period = s?.period ?? (isSample ? 2 : 1);

  const clock = useLiveCountupClock(
    s?.clock.m ?? 37,
    s?.clock.s ?? 12,
    s?.clock.running ?? false,
    s?.clock.startedAt ?? null,
  );

  const ss = (s?.sportState ?? {}) as Record<string, unknown>;
  const stoppage = typeof ss.stoppageMinutes === 'number' ? (ss.stoppageMinutes as number) : 0;
  const cards = (ss.cards as Record<string, { yellow: number; red: number }> | undefined) ?? {
    home: { yellow: 0, red: 0 },
    away: { yellow: 0, red: 0 },
  };
  const shots = (ss.shots as Record<string, number> | undefined) ?? { home: 0, away: 0 };
  const corners = (ss.corners as Record<string, number> | undefined) ?? { home: 0, away: 0 };

  const homeColor = home?.colorPrimary ?? '#16a34a';
  const awayColor = away?.colorPrimary ?? '#7c3aed';
  const homeName = home?.name ?? 'HOME';
  const awayName = away?.name ?? 'AWAY';

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black text-white">
      {!connected && !isSample && (
        <div className="absolute right-6 top-6 z-10 rounded-full bg-red-600/90 px-3 py-1 text-xs font-bold uppercase">
          Offline
        </div>
      )}

      <div className="grid h-full grid-cols-[1fr_auto_1fr]">
        {/* HOME side */}
        <div
          className="flex flex-col items-center justify-center px-12"
          style={{ background: `linear-gradient(135deg, ${homeColor}, #000)` }}
        >
          <div className="text-2xl font-bold uppercase tracking-[0.4em] opacity-80">
            {homeName}
          </div>
          <div className="my-6 font-mono text-[18rem] font-black leading-none">
            {homeScore}
          </div>
          <div className="flex items-center gap-4 text-sm">
            {cards.home?.yellow > 0 && (
              <span className="rounded-md bg-yellow-400/80 px-2 py-0.5 text-xs font-bold text-black">
                🟨 {cards.home.yellow}
              </span>
            )}
            {cards.home?.red > 0 && (
              <span className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-bold">
                🟥 {cards.home.red}
              </span>
            )}
          </div>
          <div className="mt-4 text-xs uppercase tracking-wider text-white/60">
            Shots {shots.home ?? 0} · Corners {corners.home ?? 0}
          </div>
        </div>

        {/* CENTER stack */}
        <div className="flex w-[26rem] flex-col items-center justify-center gap-8 bg-zinc-950 px-8 py-12">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.4em] text-white/40">
              {period === 1 ? '1st Half' : period === 2 ? '2nd Half' : `Period ${period}`}
            </div>
            <div className="mt-2 font-mono text-7xl font-bold tabular-nums">
              {clock.m}:{pad(clock.s)}
            </div>
            {stoppage > 0 && (
              <div className="mt-2 inline-block rounded-md bg-amber-400/20 px-3 py-1 font-mono text-xl font-bold text-amber-300">
                +{stoppage}'
              </div>
            )}
          </div>
        </div>

        {/* AWAY side */}
        <div
          className="flex flex-col items-center justify-center px-12"
          style={{ background: `linear-gradient(225deg, ${awayColor}, #000)` }}
        >
          <div className="text-2xl font-bold uppercase tracking-[0.4em] opacity-80">
            {awayName}
          </div>
          <div className="my-6 font-mono text-[18rem] font-black leading-none">
            {awayScore}
          </div>
          <div className="flex items-center gap-4 text-sm">
            {cards.away?.yellow > 0 && (
              <span className="rounded-md bg-yellow-400/80 px-2 py-0.5 text-xs font-bold text-black">
                🟨 {cards.away.yellow}
              </span>
            )}
            {cards.away?.red > 0 && (
              <span className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-bold">
                🟥 {cards.away.red}
              </span>
            )}
          </div>
          <div className="mt-4 text-xs uppercase tracking-wider text-white/60">
            Shots {shots.away ?? 0} · Corners {corners.away ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
}
